// Score clipboard. Event copies store beat offsets from an origin so a lick
// can land at the caret. A selected bar range also carries independent staff,
// harmony and form layers for whole-passage paste and Paste Special.
//
// Every spelled pitch in an event copies. Authored rests copy;
// generated padding (`r:` / `r-`) and graces do not. Paste batches each voice
// before rebuilding written durations, so tuplets never pass through a broken
// intermediate rhythm. Every operation returns one Score for one undo step.

import { passageRange, type Passage } from '../passage';
import type { IRealChord } from '../ireal/types';
import { midiOf, noteNameOf } from './pitch';
import type { SpelledPitch } from './pitch';
import { voiceTiming } from './timing';
import { insertMeasureAfter, patchMeasure, type MeasurePatch } from './formEdit';
import { recordHarmonyEdit } from './reharm';
import type {
  FormMeasure,
  NoteEvent,
  NoteEventMetadata,
  NoteHead,
  Score,
  Spanner,
  TupletGroup,
  VoiceItem,
} from './types';
import { uid } from '../song';
import { addNotes, describeEvent, type AddNoteArgs, type ContentTarget } from './voiceEdit';

interface ClipboardTuplet {
  id: string;
  actual: number;
  normal: number;
  nested: boolean;
}

export interface ClipNote {
  /** Beats from the clipboard origin. */
  at: number;
  duration: number;
  /** Null = an authored rest. */
  midi: number | null;
  /** Source identity used only to reconnect copied spanning notation. */
  sourceId?: string;
  /** Distinguishes authored rests from unpitched slash notes. */
  kind?: NoteEvent['kind'];
  spelledAs?: string;
  /** Complete voicing; optional for older single-note clipboards. No source ties. */
  pitches?: SpelledPitch[];
  /** Complete voicing and ties, clipped to endpoints inside this clipboard. */
  noteheads?: NoteHead[];
  /** Event-local notation such as articulations, dynamics, text and lyrics. */
  marks?: ClipboardEventMarks;
  tuplet?: ClipboardTuplet;
  voice: number;
}

export interface NoteClipboard {
  notes: ClipNote[];
  /** Spanners are copied only when both endpoints are present. */
  spanners?: ClipboardSpanner[];
  /** Complete selected bars; absent for an event-only copy. */
  bars?: ClipboardBar[];
}

export type ClipboardPasteMode = 'all' | 'notes' | 'harmony' | 'form';

export interface ClipboardBar {
  harmony: IRealChord[];
  form: ClipboardForm;
}

type ClipboardForm = MeasurePatch;

type ClipboardEventMarks = Pick<
  NoteEventMetadata,
  'slash' | 'cue' | 'articulations' | 'ornaments' | 'dynamic' | 'words' | 'wordsBelow' | 'lyrics'
>;

type ClipboardSpanner = Omit<Spanner, 'id'>;

const FORM_FIELDS = [
  'open',
  'close',
  'section',
  'sectionRole',
  'feel',
  'bandCue',
  'ending',
  'timeSig',
  'keySig',
  'tempo',
  'pickupBeats',
  'barRepeat',
  'coda',
  'segno',
  'fermata',
  'endMarker',
  'staffText',
  'staffTextAbove',
  'directive',
] as const satisfies readonly (keyof MeasurePatch)[];

interface LocatedNote {
  sourceId: string;
  event: NoteEvent;
  measureIndex: number;
  beat: number;
  duration: number;
  midi: number | null;
  spelledAs?: string;
  pitches: SpelledPitch[];
  tuplet?: ClipboardTuplet;
  voice: number;
}

interface EventNeighbours {
  previous?: NoteEvent;
  next?: NoteEvent;
}

function timeSigAt(score: Score, index: number): [number, number] {
  let sig: [number, number] = [4, 4];
  for (let i = 0; i <= index && i < score.form.length; i++) {
    const next = score.form[i]?.timeSig;
    if (next) sig = next;
  }
  return sig;
}

function beatsInMeasure(score: Score, index: number): number {
  return score.form[index]?.pickupBeats ?? timeSigAt(score, index)[0];
}

function absoluteBeat(score: Score, measureIndex: number, beat: number): number {
  let t = 0;
  for (let i = 0; i < measureIndex; i++) t += beatsInMeasure(score, i);
  return t + (beat - 1);
}

function locate(score: Score, absBeat: number): { measureIndex: number; beat: number } | null {
  let remaining = absBeat;
  for (let i = 0; i < score.form.length; i++) {
    const cap = beatsInMeasure(score, i);
    if (remaining < cap - 1e-9) return { measureIndex: i, beat: remaining + 1 };
    remaining -= cap;
  }
  return null;
}

function partFor(score: Score, target?: ContentTarget) {
  if (target) return score.parts.find((part) => part.id === target.partId);
  return score.parts[0];
}

function locatedFromEvent(
  score: Score,
  eventId: string,
  target?: ContentTarget,
): LocatedNote | null {
  const described = describeEvent(score, eventId, target);
  if (!described || described.duration <= 0) return null;
  if (described.rest && /^r[:-]/.test(eventId)) return null;
  const part = partFor(score, target);
  const voice = part?.measures[described.measureIndex]?.voices.find((candidate) =>
    voiceTiming(candidate).some((timed) => timed.event.id === eventId),
  );
  const timed = voice ? voiceTiming(voice).find((item) => item.event.id === eventId) : undefined;
  if (timed?.event.grace) return null;
  const group = voice?.items.find(
    (item): item is TupletGroup =>
      item.kind === 'tuplet' &&
      voiceTiming({ id: voice.id, items: [item] }).some((member) => member.event.id === eventId),
  );
  if (!timed) return null;
  const top = timed.event.notes.reduce<NoteHead | undefined>(
    (best, head) => (!best || midiOf(head.pitch) >= midiOf(best.pitch) ? head : best),
    undefined,
  );
  return {
    sourceId: eventId,
    event: timed.event,
    measureIndex: described.measureIndex,
    beat: described.beat,
    duration: described.duration,
    midi: described.midi,
    spelledAs: top ? noteNameOf(top.pitch) : undefined,
    pitches: timed.event.notes.map((head) => ({ ...head.pitch })),
    ...(group
      ? {
          tuplet: {
            id: group.id,
            actual: group.actual,
            normal: group.normal,
            nested: group.items.some((item) => item.kind === 'tuplet'),
          },
        }
      : {}),
    voice: described.voiceIndex,
  };
}

function eventNeighbours(score: Score, target?: ContentTarget): Map<string, EventNeighbours> {
  const part = partFor(score, target);
  const sequences = new Map<string, { measureIndex: number; event: NoteEvent }[]>();
  if (!part) return new Map();
  for (let measureIndex = 0; measureIndex < part.measures.length; measureIndex++) {
    const localByStaff = new Map<number, number>();
    for (const voice of part.measures[measureIndex].voices) {
      const staff = voice.staff ?? 0;
      const localVoice = localByStaff.get(staff) ?? 0;
      localByStaff.set(staff, localVoice + 1);
      if (target && staff !== target.staffIndex) continue;
      const key = `${staff}:${localVoice}`;
      const sequence = sequences.get(key) ?? [];
      for (const timed of voiceTiming(voice)) {
        if (!timed.event.grace && timed.duration.num > 0) {
          sequence.push({ measureIndex, event: timed.event });
        }
      }
      sequences.set(key, sequence);
    }
  }
  const out = new Map<string, EventNeighbours>();
  for (const sequence of sequences.values()) {
    sequence.forEach((entry, index) => {
      const previous = sequence[index - 1];
      const next = sequence[index + 1];
      out.set(entry.event.id, {
        ...(previous && entry.measureIndex - previous.measureIndex <= 1
          ? { previous: previous.event }
          : {}),
        ...(next && next.measureIndex - entry.measureIndex <= 1 ? { next: next.event } : {}),
      });
    });
  }
  return out;
}

function copiedNoteheads(
  row: LocatedNote,
  selected: ReadonlySet<string>,
  neighbours: ReadonlyMap<string, EventNeighbours>,
): NoteHead[] {
  const adjacent = neighbours.get(row.sourceId);
  return row.event.notes.map((head) => {
    const midi = midiOf(head.pitch);
    const hasIncoming =
      (head.tie === 'stop' || head.tie === 'both') &&
      !!adjacent?.previous &&
      selected.has(adjacent.previous.id) &&
      adjacent.previous.kind === 'note' &&
      adjacent.previous.notes.some(
        (candidate) =>
          midiOf(candidate.pitch) === midi &&
          (candidate.tie === 'start' || candidate.tie === 'both'),
      );
    const hasOutgoing =
      (head.tie === 'start' || head.tie === 'both') &&
      !!adjacent?.next &&
      selected.has(adjacent.next.id) &&
      adjacent.next.kind === 'note' &&
      adjacent.next.notes.some(
        (candidate) =>
          midiOf(candidate.pitch) === midi &&
          (candidate.tie === 'stop' || candidate.tie === 'both'),
      );
    const tie = hasIncoming
      ? hasOutgoing
        ? ('both' as const)
        : ('stop' as const)
      : hasOutgoing
        ? ('start' as const)
        : undefined;
    return { pitch: { ...head.pitch }, ...(tie ? { tie } : {}) };
  });
}

function copyMarks(event: NoteEvent | ClipboardEventMarks): ClipboardEventMarks {
  return {
    ...(event.slash ? { slash: event.slash } : {}),
    ...(event.cue !== undefined ? { cue: event.cue } : {}),
    ...(event.articulations ? { articulations: [...event.articulations] } : {}),
    ...(event.ornaments ? { ornaments: [...event.ornaments] } : {}),
    ...(event.dynamic ? { dynamic: event.dynamic } : {}),
    ...(event.words !== undefined ? { words: event.words } : {}),
    ...(event.wordsBelow !== undefined ? { wordsBelow: event.wordsBelow } : {}),
    ...(event.lyrics
      ? { lyrics: event.lyrics.map((syllable) => (syllable ? { ...syllable } : undefined)) }
      : {}),
  };
}

function copyHarmony(harmony: readonly IRealChord[], freshIds = false): IRealChord[] {
  return harmony.map((chord) => ({
    ...chord,
    ...(freshIds ? { id: uid('c') } : {}),
    ...(chord.alternate ? { alternate: { ...chord.alternate } } : {}),
  }));
}

function copyForm(measure: FormMeasure): ClipboardForm {
  return {
    ...(measure.open !== undefined ? { open: measure.open } : {}),
    ...(measure.close !== undefined ? { close: measure.close } : {}),
    ...(measure.section !== undefined ? { section: measure.section } : {}),
    ...(measure.sectionRole !== undefined ? { sectionRole: measure.sectionRole } : {}),
    ...(measure.feel
      ? { feel: { ...measure.feel, offset: measure.feel.offset && { ...measure.feel.offset } } }
      : {}),
    ...(measure.bandCue
      ? {
          bandCue: {
            ...measure.bandCue,
            ...(measure.bandCue.channels ? { channels: [...measure.bandCue.channels] } : {}),
            ...(measure.bandCue.duration ? { duration: { ...measure.bandCue.duration } } : {}),
          },
        }
      : {}),
    ...(measure.ending !== undefined ? { ending: measure.ending } : {}),
    ...(measure.timeSig ? { timeSig: [...measure.timeSig] as [number, number] } : {}),
    ...(measure.keySig ? { keySig: { ...measure.keySig } } : {}),
    ...(measure.tempo !== undefined ? { tempo: measure.tempo } : {}),
    ...(measure.pickupBeats !== undefined ? { pickupBeats: measure.pickupBeats } : {}),
    ...(measure.barRepeat !== undefined ? { barRepeat: measure.barRepeat } : {}),
    ...(measure.coda !== undefined ? { coda: measure.coda } : {}),
    ...(measure.segno !== undefined ? { segno: measure.segno } : {}),
    ...(measure.fermata !== undefined ? { fermata: measure.fermata } : {}),
    ...(measure.endMarker !== undefined ? { endMarker: measure.endMarker } : {}),
    ...(measure.staffText !== undefined ? { staffText: measure.staffText } : {}),
    ...(measure.staffTextAbove !== undefined ? { staffTextAbove: measure.staffTextAbove } : {}),
    ...(measure.directive !== undefined ? { directive: measure.directive } : {}),
  };
}

function copyBars(score: Score, from: number, to: number): ClipboardBar[] {
  return score.form.slice(from, to + 1).map((measure) => ({
    harmony: copyHarmony(measure.harmony),
    form: copyForm(measure),
  }));
}

function collectMeasureEvents(
  score: Score,
  measureIndex: number,
  target?: ContentTarget,
): LocatedNote[] {
  const part = partFor(score, target);
  const measure = part?.measures[measureIndex];
  if (!measure) return [];
  const out: LocatedNote[] = [];
  for (const voice of measure.voices) {
    if (target && (voice.staff ?? 0) !== target.staffIndex) continue;
    for (const timed of voiceTiming(voice)) {
      const located = locatedFromEvent(score, timed.event.id, target);
      if (located) out.push(located);
    }
  }
  return out;
}

function toClipboard(
  score: Score,
  rows: LocatedNote[],
  originAbs: number,
  target?: ContentTarget,
): NoteClipboard {
  const eligible = rows.filter(
    (row) => absoluteBeat(score, row.measureIndex, row.beat) - originAbs >= -1e-9,
  );
  const selected = new Set(eligible.map((row) => row.sourceId));
  const neighbours = eventNeighbours(score, target);
  return {
    notes: eligible
      .map((row) => ({
        at: absoluteBeat(score, row.measureIndex, row.beat) - originAbs,
        duration: row.duration,
        midi: row.midi,
        sourceId: row.sourceId,
        kind: row.event.kind,
        spelledAs: row.spelledAs,
        pitches: row.pitches.map((pitch) => ({ ...pitch })),
        noteheads: copiedNoteheads(row, selected, neighbours),
        marks: copyMarks(row.event),
        ...(row.tuplet ? { tuplet: { ...row.tuplet } } : {}),
        voice: row.voice,
      }))
      .sort((a, b) => a.at - b.at || a.voice - b.voice),
    spanners: (score.spanners ?? [])
      .filter((spanner) => selected.has(spanner.fromEventId) && selected.has(spanner.toEventId))
      .map(({ type, fromEventId, toEventId, side }) => ({
        type,
        fromEventId,
        toEventId,
        ...(side ? { side } : {}),
      })),
  };
}

/** Copy every authored event in the written bar range (origin = first bar's downbeat). */
export function copyPassage(score: Score, passage: Passage, target?: ContentTarget): NoteClipboard {
  const ids = score.form.map((measure) => measure.id);
  const range = passageRange(ids, passage);
  if (!range) return { notes: [] };
  const rows = [];
  for (let i = range.from; i <= range.to; i++) rows.push(...collectMeasureEvents(score, i, target));
  return {
    ...toClipboard(score, rows, absoluteBeat(score, range.from, 1), target),
    bars: copyBars(score, range.from, range.to),
  };
}

/** Copy named events (origin = the earliest onset). */
export function copyEvents(
  score: Score,
  ids: readonly string[],
  target?: ContentTarget,
): NoteClipboard {
  const rows = ids
    .map((id) => locatedFromEvent(score, id, target))
    .filter((row): row is LocatedNote => row != null);
  if (rows.length === 0) return { notes: [] };
  const origin = Math.min(...rows.map((row) => absoluteBeat(score, row.measureIndex, row.beat)));
  return toClipboard(score, rows, origin, target);
}

export function pasteNotes(
  score: Score,
  at: { measureIndex: number; beat: number },
  clip: NoteClipboard,
  target?: ContentTarget,
): Score | null {
  if (clip.notes.length === 0) return null;
  if (!score.form[at.measureIndex]) return null;
  const origin = absoluteBeat(score, at.measureIndex, at.beat);
  if (
    clip.notes.some(
      (note) =>
        note.tuplet && (note.tuplet.actual !== 3 || note.tuplet.normal !== 2 || note.tuplet.nested),
    )
  )
    return null;
  const groups = new Map<string, string>();
  const pastedIds = new Map<string, string>();
  const batches = new Map<string, { measureIndex: number; voice: number; notes: AddNoteArgs[] }>();
  for (const note of clip.notes) {
    const dest = locate(score, origin + note.at);
    if (!dest) continue;
    const idBase = uid('mel');
    if (note.sourceId) pastedIds.set(note.sourceId, idBase);
    const key = `${dest.measureIndex}:${note.voice}`;
    const batch = batches.get(key) ?? {
      measureIndex: dest.measureIndex,
      voice: note.voice,
      notes: [],
    };
    if (note.tuplet && !groups.has(note.tuplet.id)) groups.set(note.tuplet.id, uid('tup'));
    batch.notes.push({
      midi: note.midi ?? 60,
      idBase,
      beat: dest.beat,
      duration: note.duration,
      ...((note.kind ?? (note.midi == null ? 'rest' : 'note')) === 'rest' ? { rest: true } : {}),
      ...(note.spelledAs ? { spelledAs: note.spelledAs } : {}),
      ...(note.noteheads !== undefined
        ? {
            noteheads: note.noteheads.map((head) => ({
              ...head,
              pitch: { ...head.pitch },
            })),
          }
        : note.pitches?.length
          ? { pitches: note.pitches }
          : {}),
      ...(note.marks ? { source: copyMarks(note.marks) } : {}),
      ...(note.tuplet ? { tupletGroup: groups.get(note.tuplet.id) } : {}),
    });
    batches.set(key, batch);
  }
  let next = score;
  for (const batch of batches.values()) {
    const added = addNotes(next, batch.measureIndex, batch.notes, batch.voice, target);
    if (!added) return null;
    next = added;
  }
  if (next === score) return null;
  const copiedSpanners = (clip.spanners ?? []).flatMap((spanner): Spanner[] => {
    const fromEventId = pastedIds.get(spanner.fromEventId);
    const toEventId = pastedIds.get(spanner.toEventId);
    return fromEventId && toEventId ? [{ ...spanner, id: uid('sp'), fromEventId, toEventId }] : [];
  });
  return copiedSpanners.length
    ? { ...next, spanners: [...(next.spanners ?? []), ...copiedSpanners] }
    : next;
}

function formPatch(form: ClipboardForm): MeasurePatch {
  return {
    open: form.open,
    close: form.close,
    section: form.section,
    sectionRole: form.sectionRole,
    feel: form.feel
      ? { ...form.feel, offset: form.feel.offset && { ...form.feel.offset } }
      : undefined,
    bandCue: form.bandCue
      ? {
          ...form.bandCue,
          ...(form.bandCue.channels ? { channels: [...form.bandCue.channels] } : {}),
          ...(form.bandCue.duration ? { duration: { ...form.bandCue.duration } } : {}),
        }
      : undefined,
    ending: form.ending,
    timeSig: form.timeSig ? ([...form.timeSig] as [number, number]) : undefined,
    keySig: form.keySig ? { ...form.keySig } : undefined,
    tempo: form.tempo,
    pickupBeats: form.pickupBeats,
    barRepeat: form.barRepeat,
    coda: form.coda,
    segno: form.segno,
    fermata: form.fermata,
    endMarker: form.endMarker,
    staffText: form.staffText,
    staffTextAbove: form.staffTextAbove,
    directive: form.directive,
  };
}

function hasFormPatch(measure: FormMeasure, patch: MeasurePatch): boolean {
  return FORM_FIELDS.every(
    (field) => JSON.stringify(measure[field]) === JSON.stringify(patch[field]),
  );
}

function ensureMeasureCount(score: Score, count: number): Score {
  let next = score;
  while (next.form.length < count) {
    next = insertMeasureAfter(next, next.form.at(-1)?.id ?? null).score;
  }
  return next;
}

function pasteForm(
  score: Score,
  measureIndex: number,
  bars: readonly ClipboardBar[],
): Score | null {
  let next = score;
  for (let offset = 0; offset < bars.length; offset++) {
    const measure = next.form[measureIndex + offset];
    if (!measure) return null;
    const patch = formPatch(bars[offset].form);
    const applied = patchMeasure(next, measure.id, patch);
    if (!applied || !hasFormPatch(applied.form[measureIndex + offset], patch)) return null;
    next = applied;
  }
  return next;
}

function pasteHarmony(
  score: Score,
  measureIndex: number,
  bars: readonly ClipboardBar[],
): Score | null {
  for (let offset = 0; offset < bars.length; offset++) {
    const measure = score.form[measureIndex + offset];
    if (!measure) return null;
    const used = bars[offset].harmony.reduce((sum, chord) => sum + Math.max(0, chord.beats), 0);
    if (used > beatsInMeasure(score, measureIndex + offset) + 1e-9) return null;
  }
  const form = score.form.map((measure, index) => {
    const source = bars[index - measureIndex];
    return source ? { ...measure, harmony: copyHarmony(source.harmony, true) } : measure;
  });
  return recordHarmonyEdit(score, { ...score, form });
}

function collectEventIds(items: readonly VoiceItem[], ids: Set<string>): void {
  for (const item of items) {
    if (item.kind === 'tuplet') collectEventIds(item.items, ids);
    else ids.add(item.id);
  }
}

function clearStaffRange(
  score: Score,
  measureIndex: number,
  count: number,
  target?: ContentTarget,
): Score {
  const part = partFor(score, target);
  if (!part) return score;
  const partIndex = score.parts.indexOf(part);
  const staffIndex = target?.staffIndex ?? 0;
  const removed = new Set<string>();
  const measures = part.measures.map((measure, index) => {
    if (index < measureIndex || index >= measureIndex + count) return measure;
    const voices = measure.voices.filter((voice) => {
      if ((voice.staff ?? 0) !== staffIndex) return true;
      collectEventIds(voice.items, removed);
      return false;
    });
    return voices.length === measure.voices.length ? measure : { ...measure, voices };
  });
  if (removed.size === 0) return score;
  const parts = score.parts.map((candidate, index) =>
    index === partIndex ? { ...part, measures } : candidate,
  );
  const spanners = score.spanners?.filter(
    (spanner) => !removed.has(spanner.fromEventId) && !removed.has(spanner.toEventId),
  );
  return spanners && spanners.length !== score.spanners!.length
    ? { ...score, parts, spanners }
    : { ...score, parts };
}

/** Paste selected clipboard layers as one immutable score operation. */
export function pasteClipboard(
  score: Score,
  at: { measureIndex: number; beat: number },
  clip: NoteClipboard,
  mode: ClipboardPasteMode = 'all',
  target?: ContentTarget,
): Score | null {
  const bars = clip.bars ?? [];
  const effectiveMode = mode === 'all' && bars.length === 0 ? 'notes' : mode;
  if (effectiveMode !== 'notes' && bars.length === 0) return null;
  const requiredMeasures =
    at.measureIndex + bars.length + (effectiveMode === 'notes' && at.beat > 1 ? 1 : 0);
  let next = bars.length > 0 ? ensureMeasureCount(score, requiredMeasures) : score;
  if (effectiveMode === 'all') next = clearStaffRange(next, at.measureIndex, bars.length, target);
  if (effectiveMode === 'all' || effectiveMode === 'form') {
    const structured = pasteForm(next, at.measureIndex, bars);
    if (!structured) return null;
    next = structured;
  }
  if (effectiveMode === 'all' || effectiveMode === 'harmony') {
    const harmonized = pasteHarmony(next, at.measureIndex, bars);
    if (!harmonized) return null;
    next = harmonized;
  }
  if (effectiveMode === 'all' || effectiveMode === 'notes') {
    if (clip.notes.length > 0) {
      const pasted = pasteNotes(
        next,
        { measureIndex: at.measureIndex, beat: effectiveMode === 'all' ? 1 : at.beat },
        clip,
        target,
      );
      if (!pasted) return null;
      next = pasted;
    } else if (effectiveMode === 'notes') {
      return null;
    }
  }
  return next === score ? null : next;
}

/** Cut musical content from selected bars while retaining their measure grid. */
export function cutPassage(score: Score, passage: Passage, target?: ContentTarget): Score | null {
  const range = passageRange(
    score.form.map((measure) => measure.id),
    passage,
  );
  if (!range) return null;
  const cleared = clearStaffRange(score, range.from, range.to - range.from + 1, target);
  const form = cleared.form.map((measure, index) =>
    index >= range.from && index <= range.to && measure.harmony.length > 0
      ? { ...measure, harmony: [] }
      : measure,
  );
  const next = form.some((measure, index) => measure !== cleared.form[index])
    ? recordHarmonyEdit(cleared, { ...cleared, form })
    : cleared;
  return next === score ? null : next;
}
